<template>
  <el-popover
    v-model="visible"
    v-loading="loading.popover || propLoading"
    :popper-class="`schedulerPopover ${$mq}`"
    :placement="'bottom-start'"
    :visible-arrow="false"
    trigger="click"
    :disabled="disabled"
    @show="showPopover"
  >
    <el-row
      :gutter="30"
      class="main"
      :class="$mq"
    >
      <el-col
        v-loading="loading.day"
        :span="responsiveCol.one"
        class="left"
        :class="`${$mq} ${['mobile', 'tablet'].includes($mq) ? 'mobilePicker' : 'deskPicker'}`"
      >
        <Datepicker
          v-if="availableDay"
          v-model="selectedDate"
          :inline="true"
          :disabled-dates="disabledDates"
          :monday-first="true"
          :open-date="openDate"
          calendar-class="coach-calendar"
          :use-utc="false"
          @selected="getAvailableTime"
        />
      </el-col>
      <el-col
        v-loading="loading.time"
        :span="responsiveCol.two"
        class="right"
        :class="`${$mq} ${['mobile', 'tablet'].includes($mq) ? 'mobileDate' : 'deskDate'}`"
      >
        <div
          class="timeSelectTitle defaultCursor"
          :class="['mobile', 'tablet'].includes($mq) ? 'mobileTitle' : 'deskTitle'"
        >
          Available starting time for
          <b v-if="selectedDate">{{ selectedDate | moment("ddd, MMM DD, YYYY") }}</b>
          <b v-else>Selected Date</b>
        </div>
        <el-row
          :gutter="20"
          class="timeLayout"
        >
          <div
            v-if="!selectedDate"
            class="placeTime defaultCursor"
          >
            <i class="el-icon-info"></i>
            {{ $t('leader.coach.selectDate') }}
          </div>

          <div
            v-else-if="getTimeLength === 0"
            class="placeTime defaultCursor"
          >
            <i class="el-icon-info"></i>
            {{ $t('leader.coach.noMoreSlot') }}
          </div>

          <div
            v-else-if="['desktop', 'laptop'].includes($mq)"
            class="timeLayoutBig"
          >
            <el-col
              :span="12"
              :class="$mq"
            >
              <el-radio-group
                v-model="selectedTime"
                @change="scheduleMeeting"
              >
                <ul>
                  <li
                    v-for="(time, DAkey) in availableTimeAM"
                    :key="`DeskAM${DAkey}`"
                  >
                    <el-radio-button
                      :label="time"
                    />
                  </li>
                </ul>
              </el-radio-group>
            </el-col>
            <el-col
              :span="12"
              :class="$mq"
            >
              <el-radio-group
                v-model="selectedTime"
                @change="scheduleMeeting"
              >
                <ul>
                  <li
                    v-for="(time, DPkey) in availableTimePM"
                    :key="`DeskPM${DPkey}`"
                  >
                    <el-radio-button
                      :label="time"
                    />
                  </li>
                </ul>
              </el-radio-group>
            </el-col>
          </div>

          <div
            v-else
            class="timeLayoutSmall"
          >
            <el-col :span="24">
              <el-radio-group
                v-model="selectedTime"
                @change="scheduleMeeting"
              >
                <ul class="timeSmall">
                  <li
                    v-for="(time, MAkey) in availableTimeAM"
                    :key="`MobileAM${MAkey}`"
                  >
                    <el-radio-button
                      :label="time"
                    />
                  </li>
                  <li
                    v-for="(time, MPkey) in availableTimePM"
                    :key="`MobilePM${MPkey}`"
                  >
                    <el-radio-button
                      :label="time"
                    />
                  </li>
                </ul>
              </el-radio-group>
            </el-col>
          </div>
        </el-row>
      </el-col>
    </el-row>

    <el-input
      slot="reference"
      class="schedulerInput"
      :class="$mq"
      :value="meetingStatus === 'cancelled' ? null : formattedDate"
      :placeholder="placeholder"
      :readonly="true"
      :suffix-icon="icon"
      :disabled="disabled"
    />
  </el-popover>
</template>

<script>
import { mapState } from 'vuex';
import Datepicker from 'vuejs-datepicker';
import api from '@/api';

export default {
  components: {
    Datepicker,
  },

  props: {
    coachId: {
      type: String,
      default: '',
    },

    coachName: {
      type: String,
      default: '',
    },

    meetingTime: {
      type: String,
      default: '',
    },

    meetingStatus: {
      type: String,
      default: '',
    },

    meetingUuid: {
      type: String,
      default: '',
    },

    placeholder: {
      type: String,
      default: 'Please input',
    },

    resetDate: {
      type: Boolean,
      default: false,
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    meetingType: {
      type: String,
      default: 'chem',
    },

    pulseDebriefWarn: {
      type: Boolean,
      default: false,
    },

    icon: {
      type: String,
      default: 'el-icon-date',
    },

    propLoading: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      visible: false,
      availableDay: null,
      next_avail_date: '',
      exception_dates: null,
      availableTime: null,
      selectedDate: null,
      selectedTime: null,
      loading: {
        popover: false,
        day: false,
        time: false,
      },
    };
  },

  computed: {
    ...mapState({
      userTimezone: (state) => state.user.user.time_zone,
    }),

    disabledDays() {
      const days = [0, 1, 2, 3, 4, 5, 6];
      if (this.availableDay) {
        this.availableDay.forEach((day) => {
          let index;
          switch (day) {
            case 'Monday':
              index = days.indexOf(1);
              if (index > -1) {
                days.splice(index, 1);
              }
              break;
            case 'Tuesday':
              index = days.indexOf(2);
              if (index > -1) {
                days.splice(index, 1);
              }
              break;
            case 'Wednesday':
              index = days.indexOf(3);
              if (index > -1) {
                days.splice(index, 1);
              }
              break;
            case 'Thursday':
              index = days.indexOf(4);
              if (index > -1) {
                days.splice(index, 1);
              }
              break;
            case 'Friday':
              index = days.indexOf(5);
              if (index > -1) {
                days.splice(index, 1);
              }
              break;
            case 'Saturday':
              index = days.indexOf(6);
              if (index > -1) {
                days.splice(index, 1);
              }
              break;
            case 'Sunday':
              index = days.indexOf(0);
              if (index > -1) {
                days.splice(index, 1);
              }
              break;
            default:
          }
        });
      }

      return days;
    },

    disabledDates() {
      let minDate;
      if (this.next_avail_date) {
        minDate = this.$moment(this.next_avail_date).toDate();
      } else {
        minDate = this.$moment().toDate();
      }

      const that = this;
      return {
        to: minDate,
        // days: this.disabledDays,
        customPredictor(date) {
          const d = that.$moment(date);
          // Allow days with exceptions
          if (that.exception_dates.some((el) => el.date === d.format('YYYY-MM-DD') && el.hours.length > 0)) {
            return false;
          }
          // disables the date if it is in disabled day list
          if (!that.disabledDays.includes(d.day())) {
            return false;
          }
          return true;
        },
      };
    },

    availableTimeAM() {
      let times = [];
      if (this.availableTime) {
        times = this.availableTime.filter((el) => parseInt(el.split(':')[0], 10) < 13);
      }
      return times;
    },

    availableTimePM() {
      let times = [];
      if (this.availableTime) {
        times = this.availableTime.filter((el) => parseInt(el.split(':')[0], 10) >= 13);
      }
      return times;
    },

    getTimeLength() {
      return this.availableTimeAM.length + this.availableTimePM.length;
    },

    formattedDate: {
      get() {
        if (this.selectedDate && this.selectedTime) {
          const dateFormatted = this.$moment(this.selectedDate).format('YYYY-MM-DD');
          const dateTime = this.$moment(`${dateFormatted} ${this.selectedTime}`).format('YYYY-MM-DD HH:mm');
          return dateTime;
        }
        return '';
      },
      set(newValue) {
        if (newValue) {
          // '2019-06-15 07:30:00'
          const date = newValue.split(' ');
          this.selectedDate = new Date(`${date[0]}T${date[1]}`);

          const time = date[1].split(':');
          // eslint-disable-next-line prefer-destructuring
          this.selectedTime = `${time[0]}:${time[1]}`;
        } else {
          this.selectedDate = null;
          this.selectedTime = null;
        }
      },
    },

    responsiveCol() {
      const col = {};
      if (['mobile', 'tablet'].includes(this.$mq)) {
        col.one = 24;
        col.two = 24;
      } else {
        col.one = 13;
        col.two = 11;
      }
      return col;
    },

    openDate() {
      if (this.next_avail_date) {
        return this.$moment(this.next_avail_date).toDate();
      }
      return this.$moment().toDate();
    },
  },

  watch: {
    meetingTime() {
      // '2019-06-15 07:30:00+00:00'
      if (this.meetingTime && this.meetingTime.indexOf('+')) {
        [this.formattedDate] = this.meetingTime.split('+');
      } else {
        this.formattedDate = this.meetingTime;
      }
      this.getAvailableTime(this.selectedDate);
    },
  },

  created() {
    if (this.meetingTime) {
      // '2019-06-15 07:30:00+00:00'
      if (this.meetingTime.indexOf('+')) {
        [this.formattedDate] = this.meetingTime.split('+');
      } else {
        this.formattedDate = this.meetingTime;
      }
      this.getAvailableTime(this.selectedDate);
    }
  },

  methods: {
    async getAvailableDay() {
      if (this.visible) {
        try {
          this.loading.day = true;
          let response;
          if (this.coachId) {
            response = await api.getCoachAvailableDay(this.coachId);
          } else {
            response = await api.getMyCoachAvailableDay();
          }
          const { data } = response;
          this.availableDay = data.days ? data.days : [];
          this.exception_dates = data.exception_dates ? data.exception_dates : null;
          if (data?.next_avail_date) {
            const today = new Date();
            if (this.$moment(data.next_avail_date).isBefore(today)) {
              this.next_avail_date = this.$moment(today)
                .add(1, 'days')
                .format('YYYY-MM-DD');
            } else {
              this.next_avail_date = data.next_avail_date;
            }
          } else {
            this.next_avail_date = null;
          }
        } catch (err) {
          if (err.msg) {
            console.log(err.msg);
            // this.$message.error(err.msg);
          }
        } finally {
          this.loading.day = false;
        }
      }
    },

    async getAvailableTime(date) {
      if (this.visible) {
        this.availableTime = null;
        this.selectedTime = null;

        try {
          this.loading.time = true;
          let response;
          if (this.coachId) {
            response = await api.getCoachAvailableTime(this.coachId, {
              search_date: this.$moment(date).format('YYYY-MM-DD'),
            });
          } else {
            response = await api.getMyCoachAvailableTime({
              search_date: this.$moment(date).format('YYYY-MM-DD'),
            });
          }

          const res = response.data;
          if (res && res.length) {
            res.sort((a, b) => {
              if (a.split(':')[0] * 1 === b.split(':')[0] * 1) {
                return a.split(':')[1] * 1 - b.split(':')[1] * 1;
              }
              return a.split(':')[0] * 1 - b.split(':')[0] * 1;
            });
          }

          this.availableTime = res;
        } catch (err) {
          if (err.msg) {
            console.log(err.msg);
            // this.$message.error(err.msg);
          }
        } finally {
          this.loading.time = false;
        }
      }
    },

    showPopover() {
      this.getAvailableDay();
      if (this.selectedDate) {
        let time;
        if (this.selectedTime) time = this.selectedTime;
        this.getAvailableTime(this.selectedDate);
        if (time) this.selectedTime = time;
      }
    },

    async scheduleMeeting() {
      const isLessThan48Hours = this.$moment()
        .tz(this.userTimezone)
        .diff(this.meetingTime, 'hours') > -48;
      console.log(isLessThan48Hours);

      try {
        this.loading.popover = true;
        let response;
        switch (this.meetingType) {
          case 'chem':
            try {
              await this.$confirm(
                this.$t('leader.coach.confirmChemBook', {
                  coachName: this.coachName,
                  date: this.formattedDate,
                }),
                this.$t('leader.coach.confirmation'),
                {
                  confirmButtonText: this.$t('leader.coach.book'),
                  cancelButtonText: this.$t('common.goBack'),
                },
              );
              response = await api.scheduleChemMeeting(this.coachId, {
                start_time: this.formattedDate,
              });
            } catch (err) {
              if (err === 'cancel') {
                this.clearInput();
              } else if (err.key === 'maxChemSessionBooked') {
                this.$alert(this.$t('leader.coach.reachedMaximum', {
                  email: 'Help@GlobalCoachGroup.com',
                }), {
                  confirmButtonText: this.$t('common.ok'),
                }).then(() => {
                  this.clearInput();
                });
              } else if (err.msg) {
                this.$message.error(err.msg);
                this.clearInput();
              }
            }
            break;
          case 'nextSession':
            try {
              if (isLessThan48Hours) {
                const reason = await this.$prompt(
                  this.$t('leader.coach.rescheduleLess48'),
                  {
                    inputType: 'textarea',
                    showClose: false,
                    showCancelButton: true,
                    confirmButtonText: this.$t('leader.coach.rescheduleMeeting'),
                    cancelButtonText: this.$t('common.goBack'),
                    roundButton: true,
                    cancelButtonClass: 'el-button--info promptBtn',
                    confirmButtonClass: 'promptBtn',
                    customClass: 'coachMsgBox',
                    inputValidator: (value) => {
                      if (!value) {
                        return this.$t('leader.coach.pleaseInputReason');
                      }
                      return true;
                    },
                    inputErrorMessage: this.$t('leader.coach.invalidReason'),
                  },
                );
                response = await api.bookNextSession({
                  start_time: this.formattedDate,
                  reason: reason.value,
                });
              } else {
                if (this.pulseDebriefWarn) {
                  await this.$confirm(this.$t('leader.coach.confirmPulseDebrief'), '', {
                    confirmButtonText: this.$t('leader.coach.yesBook'),
                    cancelButtonText: this.$t('common.cancel'),
                    cancelButtonClass: 'el-button--info promptBtn',
                    confirmButtonClass: 'promptBtn',
                  });
                } else {
                  await this.$confirm(
                    this.$t('leader.coach.confirmNext', {
                      coachName: this.coachName,
                      date: this.formattedDate,
                    }),
                    this.$t('leader.coach.confirmation'),
                    {
                      confirmButtonText: this.$t('leader.coach.book'),
                      cancelButtonText: this.$t('common.goBack'),
                    },
                  );
                }

                response = await api.bookNextSession({
                  start_time: this.formattedDate,
                });
              }
              this.selectedDate = null;
              this.selectedTime = null;
              this.$emit('meetingCreated', response.data);
            } catch (err) {
              console.log(err);
              if (err === 'cancel') {
                this.clearInput();
              } else if (err.key === 'maxChemSessionBooked') {
                this.$alert(this.$t('leader.coach.reachedMaximum', {
                  email: 'Help@GlobalCoachGroup.com',
                }), {
                  confirmButtonText: this.$t('common.ok'),
                }).then(() => {
                  this.clearInput();
                });
              } else if (err.msg) {
                this.$message.error(err.msg);
                this.clearInput();
              }
            }
            break;
          case 'rescheduleSession':
            try {
              if (isLessThan48Hours) {
                const reason = await this.$prompt(
                  this.$t('leader.coach.rescheduleLess48'),
                  {
                    inputType: 'textarea',
                    showClose: false,
                    showCancelButton: true,
                    confirmButtonText: this.$t('leader.coach.rescheduleMeeting'),
                    cancelButtonText: this.$t('common.goBack'),
                    roundButton: true,
                    cancelButtonClass: 'el-button--info promptBtn',
                    confirmButtonClass: 'promptBtn',
                    customClass: 'coachMsgBox',
                    inputValidator: (value) => {
                      if (!value) {
                        return this.$t('leader.coach.pleaseInputReason');
                      }
                      return true;
                    },
                    inputErrorMessage: this.$t('leader.coach.invalidReason'),
                  },
                );
                response = await api.rescheduleNextSession({
                  start_time: this.formattedDate,
                  reason: reason.value,
                });
              } else {
                await this.$confirm(
                  this.$t('leader.coach.confirmReschedule', {
                    coachName: this.coachName,
                    date: this.formattedDate,
                  }),
                  this.$t('leader.coach.confirmation'),
                  {
                    confirmButtonText: this.$t('leader.coach.book'),
                    cancelButtonText: this.$t('common.goBack'),
                  },
                );
                response = await api.rescheduleNextSession({
                  start_time: this.formattedDate,
                });
              }

              if (this.resetDate) {
                this.selectedDate = null;
                this.selectedTime = null;
              } else {
                this.formattedDate = this.meetingTime;
              }
              this.$emit('meetingCreated', response.data);
            } catch (err) {
              if (err === 'cancel') {
                this.clearInput();
              } else if (err.key === 'maxChemSessionBooked') {
                this.$alert(this.$t('leader.coach.reachedMaximum', {
                  email: 'Help@GlobalCoachGroup.com',
                }), {
                  confirmButtonText: this.$t('common.ok'),
                }).then(() => {
                  this.clearInput();
                });
              } else if (err.msg) {
                this.$message.error(err.msg);
                this.clearInput();
              }
            }
            break;

          default:
            throw new Error('unrecognizedMeetingType');
        }

        this.visible = false;
        this.$emit('update:meetingTime', response.data.start_time);
        this.$emit('update:meetingStatus', response.data.status);
        this.$emit('update:meetingUuid', response.data.uuid);
      } catch (err) {
        if (err.msg) {
          this.$message.error(err.msg);
        } else if (err.message === 'unrecognizedMeetingType') {
          this.$message.error(this.$t('leader.coach.unrecognizedType'));
        }
      } finally {
        this.loading.popover = false;
      }
    },

    clearInput() {
      if (this.meetingTime) {
        // eslint-disable-next-line prefer-destructuring
        this.selectedTime = this.meetingTime.split(' ')[1];
      } else {
        this.selectedTime = null;
      }
    },
  },
};
</script>

<style lang="scss">
.schedulerPopover {
  padding: 0;
  max-width: 85%;

  &.mobile,
  &.tablet {
    left: 50% !important;
    top: 15vh !important;
    transform: translateX(-50%) !important;
    max-height: calc(100vh - 200px);
    overflow-y: auto;
    width: 100%;
    max-width: 330px;
    min-width: 320px;
  }

  .el-row.main {
    margin: 0 !important;
    width: 100%;
    padding: 15px 0;

    &.mobile,
    &.tablet {
      padding: 0;
    }

    .timeLayout {
      margin: 0 !important;

      .timeLayoutBig {
        .el-radio-button {
          width: 80%;
        }
      }

      .placeTime {
        width: 100%;
        font-size: 14px;
        text-align: center;
        padding: 30px 0;
        color: #82848a;
      }
    }

    .el-col {
      // max-height: 320px;
      max-width: 100%;

      .vdp-datepicker {
        max-width: 100%;
        font-family: 'Montserrat', 'Avenir', Helvetica, Arial, sans-serif;

        .coach-calendar {
          max-width: 100%;
          border: none;
          margin: 0 auto;
        }
      }

      &.mobile,
      &.tablet {
        // width: 330px;
        max-width: 100%;
        float: unset;

        .vdp-datepicker {
          max-width: 100%;
          font-family: 'Montserrat', 'Avenir', Helvetica, Arial, sans-serif;
        }
      }

      &.right {
        overflow-y: auto;
        font-family: 'Montserrat', 'Avenir', Helvetica, Arial, sans-serif;
        font-size: 13px;
        word-break: break-word;
        max-height: 335px;

        ul {
          padding: 0;
        }

        .timeSelectTitle {
          box-sizing: border-box;

          &.deskTitle {
            padding: 5px 5px 0;
          }

          &.mobileTitle {
            padding: 15px 15px 15px;
          }
        }
      }

      &.left {
        padding: 0 10px !important;
        min-width: 250px;
        min-height: 250px;
      }

      .el-radio-group {
        width: 100%;

        li {
          margin-top: 5px;

          .el-radio-button__inner {
            padding: 5px 20px;
            line-height: 12px;
            width: 100%;
          }
        }

        .timeSmall {
          box-sizing: border-box;
          padding: 0 15px 15px;
          display: flex;
          flex-wrap: wrap;

          li {
            display: inline-block;
            width: calc(100% / 3);
            text-align: center;

            .el-radio-button {
              min-width: 85px;
            }
          }
        }
      }

      &.deskDate {
        padding: 0 15px;
      }

      &.mobileDate {
        padding: 0 !important;

        .timeLayout {
          .timeLayoutSmall {
            .el-col {
              padding: 0 !important;
            }
          }
        }
      }
    }
  }
}

.schedulerInput {
  .el-input__inner {
    cursor: pointer;
    border-radius: 20px;
  }
}

.cancelledMeeting {
  .el-input__inner {
    padding-right: 30px;

    &.mobile,
    &.tablet {
      padding-right: 15px;
    }
  }
}
</style>
